import React, { Fragment, useCallback, useState, useMemo, ChangeEventHandler } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import ExperimentItem from './ExperimentItem'
import FeatureFlagItem from './FeatureFlagItem'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { EmptyObject, objectEntries } from 'lib/object/objectUtils'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import { sortBy } from 'lib/array/arrayUtils'
import Accordion from 'LuxKit/Accordion'
import { ExperimentEntry, FeatureFlagEntry, filterOptimizelyEntry } from './ExperimentPanelHelpers'

function ExperimentControlPanel() {
  const optimizelyFeatureFlags = useAppSelector(state => state.optimizely.optimizelyFeatureFlags ?? EmptyObject)
  const optimizelyExperiments = useAppSelector(state => state.optimizely.optimizelyExperiments ?? EmptyObject)
  const [searchValue, setSearchValue] = useState('')

  const experimentList = useMemo<Array<ExperimentEntry>>(() => {
    return sortBy(
      objectEntries(optimizelyExperiments),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is ExperimentEntry => {
      const [, exp] = entry
      return exp !== undefined
    })
  }, [optimizelyExperiments])

  const featureFlagList = useMemo<Array<FeatureFlagEntry>>(() => {
    return sortBy(
      objectEntries(optimizelyFeatureFlags),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is FeatureFlagEntry => entry !== undefined)
  }, [optimizelyFeatureFlags])

  const filteredExperiments = useMemo<Array<ExperimentEntry>>(() => {
    return experimentList.filter(filterOptimizelyEntry(searchValue))
  }, [searchValue, experimentList])

  const filteredFeatureFlags = useMemo<Array<FeatureFlagEntry>>(() => {
    return featureFlagList.filter(filterOptimizelyEntry(searchValue))
  }, [featureFlagList, searchValue])

  const hasFeatureFlags = Object.keys(optimizelyFeatureFlags).length > 0
  const hasExperiments = Object.keys(optimizelyExperiments).length > 0

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
  }, [])

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setSearchValue(e.target.value)
  }, [])

  return (
    <ModalBase mode="drawer-starboard">
      <ModalHeader title="AB Control Panel">
        <SearchFormTextField
          label="Search experiments"
          placeholder="search by key or name"
          value={searchValue}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
        />
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <Accordion heading="Experiments" defaultOpen hideDivider size="large">
            {!hasExperiments && <BodyText variant="large">No experiments found.</BodyText>}

            {hasExperiments && (
              <VerticalSpacer gap={12}>
                {filteredExperiments.length === 0 && <BodyText variant="large">No matching experiments</BodyText>}
                {filteredExperiments.map(([experimentId, experiment], index) => (
                  <Fragment key={experimentId}>
                    {index > 0 && <Divider kind="secondary" />}
                    <ExperimentItem
                      experimentId={experimentId}
                      experiment={experiment}
                    />
                  </Fragment>
                ))}
              </VerticalSpacer>
            )}
          </Accordion>
          <Accordion heading="Feature Flags" defaultOpen hideDivider size="large">
            {!hasFeatureFlags && <BodyText variant="large">No feature flags found.</BodyText>}

            {hasFeatureFlags && (
              <VerticalSpacer gap={12}>
                {filteredFeatureFlags.length === 0 && <BodyText variant="large">No matching feature flags</BodyText>}
                {filteredFeatureFlags.map(([flagKey, enabled], index) => (
                  <Fragment key={flagKey}>
                    {index > 0 && <Divider kind="secondary" />}
                    <FeatureFlagItem flagKey={flagKey} enabled={enabled} />
                  </Fragment>
                ))}
              </VerticalSpacer>
            )}
          </Accordion>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default ExperimentControlPanel
